/**
 * Lista FIXA de linhas do Inventário de Riscos (Seção 18) — uma tabela por Cargo (GHE =
 * Setor + Cargo, ver `sections/18.InventarioRiscosSection.js`), sempre com estas mesmas
 * 13 linhas, nesta mesma ordem, independentemente do que a atividade respondeu.
 *
 * Transcrito por inspeção pixel-a-pixel do modelo de referência real (56 páginas,
 * Seção 18 nas páginas 42-51 — três blocos completos: "Gerente Agência", "Gerencia
 * Negócios e Atendimento" e "Supervisor Administrativo") — Possível Risco, Tipo de
 * exposição, Fonte geradora, Possíveis lesões/agravos, Embasam. Técnico e Medidas de
 * controle existentes são texto INSTITUCIONAL FIXO do documento, idêntico em toda
 * ocorrência de cada linha, em qualquer Cargo. As únicas colunas dinâmicas por
 * Cargo/atividade são Nº func. expostos (`cargoEngine.js`, nível de Cargo, não de
 * linha) e Probabilidade/Severidade/Nível de Risco (por linha — ver `cargoEngine.js`).
 *
 * `\n` marca quebras de linha que o modelo real mostra dentro da própria célula
 * (renderizadas com `preLine` em `RedDataCell`, ver Seção 18) — nunca reformatar essas
 * strings para uma única linha corrida.
 *
 * `tipo` indica a estratégia de resolução de Probabilidade/Severidade em
 * `cargoEngine.js`:
 * - 'conforto': valor único por Cargo (Ruído/Temperatura/Iluminância — pergunta
 *   dedicada por posição de Cargo + regra de faixa, ver `riskEngine.js`).
 * - 'catalogo': procurado entre as respostas de "Possível Risco" da atividade para
 *   este Cargo, casando pelo nome (ver `INVENTARIO_PROBABILIDADE_QUESTION_IDS` /
 *   `INVENTARIO_SEVERIDADE_QUESTION_IDS` em `mappingIds.js`).
 * - 'psicossocial': igual a 'catalogo' na busca, mas o par Probabilidade/Severidade
 *   encontrado (ou o default de ausência) é resolvido pela Matriz Final própria em
 *   `psicossocialRiskEngine.js` — NUNCA pela Matriz de Risco principal.
 *
 * ABERTO (ver descrição do PR): o modelo de referência mostra a linha "Fatores
 * Psicossociais relacionados ao trabalho" duplicada (2x, conteúdo idêntico) nos blocos
 * "Gerente Agência" e "Gerencia Negócios e Atendimento", mas só 1x no bloco "Supervisor
 * Administrativo" — inconsistência do próprio modelo (não deu para confirmar se é
 * intencional ou um artefato de composição manual do documento de referência). Mantida
 * aqui como 2 entradas idênticas (maioria dos exemplos), a validar com a cliente.
 */
export const FATORES_PSICOSSOCIAIS_NOME = 'Fatores Psicossociais relacionados ao trabalho'

const linhaFatoresPsicossociais = {
  possivelRisco: FATORES_PSICOSSOCIAIS_NOME,
  tipoExposicao: 'Ergonômico\n(Cognitivo e Organizacional)',
  tipo: 'psicossocial',
  fonteGeradora: 'Conteúdo do Trabalho\nContexto do Trabalho\nGestão de metas e autonomia',
  possiveisLesoes: 'Distúrbios Psíquicos',
  embasamentoTecnico: 'Quantitativo - AET\nErgocheck-Premapa\nItens: 1.5.3.1.4 e 1.5.3.2.1 da NR1',
  medidasControle:
    'Medidas organizacionais e administrativas, incluindo canais institucionais, fortalecimento e a ' +
    'capacitação contínua de lideranças, além de ações preventivas estruturadas, em conformidade com a ' +
    'avaliação de risco.'
}

export const LINHAS_INVENTARIO_RISCOS_FIXAS = [
  {
    possivelRisco: 'Ruído',
    tipoExposicao: 'Ergonômico\n(conforto)',
    tipo: 'conforto-ruido',
    fonteGeradora: 'Ruído de fundo, conversas',
    possiveisLesoes: 'Desconforto, falta de concentração',
    embasamentoTecnico: 'NR-17 - NHO 01',
    medidasControle: 'Monitoramento bianual conforme vigência do PGR'
  },
  {
    possivelRisco: 'Temperatura',
    tipoExposicao: 'Ergonômico\n(conforto)',
    tipo: 'conforto-temperatura',
    fonteGeradora: 'Ventilação forçada através de sistemas de ar-condicionado',
    possiveisLesoes: 'Irritação de mucosas e vias aéreas\nDesconforto osteoarticular',
    embasamentoTecnico: 'NR-17 - NHO 11',
    medidasControle:
      'Monitoramento bianual conforme vigência do PGR\nManter o sistema de ventilação forçada funcionando ' +
      'adequadamente, Executar e acompanhar as medidas de controle do PMOC'
  },
  {
    possivelRisco: 'Iluminância',
    tipoExposicao: 'Ergonômico\n(conforto)',
    tipo: 'conforto-iluminancia',
    fonteGeradora: 'Sistemas de iluminação do ambiente fonte natural e artificial',
    possiveisLesoes: 'Desconforto, falta de concentração',
    embasamentoTecnico: 'NR 17 - NHO 11',
    medidasControle: 'Monitoramento bianual conforme vigência do PGR'
  },
  {
    possivelRisco: 'Mov. Manual de Cargas - Levantamento e abaixamento (MMC)',
    tipoExposicao: 'Ergonômico\n(Biomecânico)',
    tipo: 'catalogo',
    fonteGeradora: 'Carga > = 3Kg',
    possiveisLesoes: 'Distúrbios osteomusculares\nColuna Lombar',
    embasamentoTecnico: 'Se Qualitativo - AEP\nSe Quantitativo - AET',
    medidasControle: 'Não aplicáveis medidas de controle devido inexistência do fator de risco'
  },
  {
    possivelRisco: 'Mov. Manual de Cargas – Transporte',
    tipoExposicao: 'Ergonômico\n(Biomecânico)',
    tipo: 'catalogo',
    fonteGeradora: 'Carga > = 3Kg, transportada por mais de 1m',
    possiveisLesoes: 'Distúrbios osteomusculares\nColuna Lombar',
    embasamentoTecnico: 'Se Qualitativo - AEP\nSe Quantitativo - AET',
    medidasControle: 'Não aplicáveis medidas de controle devido inexistência do fator de risco'
  },
  {
    possivelRisco: 'Mov. Manual de Cargas - Puxar e Empurrar',
    tipoExposicao: 'Ergonômico\n(Biomecânico)',
    tipo: 'catalogo',
    fonteGeradora: 'Puxar e empurrar realizando deslocamento de todo o corpo',
    possiveisLesoes: 'Distúrbios osteomusculares\nColuna Lombar',
    embasamentoTecnico: 'Se Qualitativo - AEP\nSe Quantitativo - AET',
    medidasControle: 'Não aplicáveis medidas de controle devido inexistência do fator de risco'
  },
  {
    possivelRisco: 'Repetitividade',
    tipoExposicao: 'Ergonômico\n(Biomecânico)',
    tipo: 'catalogo',
    fonteGeradora: 'Tarefas em ciclos, independente do tempo de duração, por mais de 30% da jornada (ISO 11228-3/2026)',
    possiveisLesoes: 'Distúrbios osteomusculares\nMembros superiores',
    embasamentoTecnico: 'Se Qualitativo - AEP\nSe Quantitativo - AET',
    medidasControle:
      'Pausas entre as tarefas, múltiplas tarefas dentro da jornada, alternância de postura, orientação ' +
      'postural, ISSO (Instrução de Segurança e Saúde Ocupacional)'
  },
  {
    possivelRisco: 'Postura Estática',
    tipoExposicao: 'Ergonômico\n(Biomecânico)',
    tipo: 'catalogo',
    fonteGeradora:
      'Postura de trabalho estática (cabeça, pescoço, tronco/membros superiores) mantidos por mais de 4seg e ' +
      'repetidos ao longo da jornada',
    possiveisLesoes: 'Distúrbios osteomusculares\nMembros superiores e/ou inferiores/ Coluna Cervical e Lombar',
    embasamentoTecnico: 'Se Qualitativo - AEP\nSe Quantitativo - AET',
    medidasControle:
      'Pausas entre as tarefas, múltiplas tarefas dentro da jornada, alternância de postura, orientação ' +
      'postural, ISSO (Instrução de Segurança e Saúde Ocupacional)'
  },
  // Fatores Psicossociais — mantida 2x, ver ABERTO acima.
  linhaFatoresPsicossociais,
  linhaFatoresPsicossociais,
  {
    possivelRisco: 'Batidas contra',
    tipoExposicao: 'Acidente',
    tipo: 'catalogo',
    fonteGeradora: 'Arranjo físico inadequado\nAto inseguro',
    possiveisLesoes: 'Torções, contusões, luxações e fraturas',
    embasamentoTecnico: '1.5.4.3.2 da NR 1',
    medidasControle: 'ISSO (Instrução de Segurança e Saúde Ocupacional)\nSinalizações de segurança sempre que necessário.'
  },
  {
    possivelRisco: 'Queda de mesmo nível',
    tipoExposicao: 'Acidente',
    tipo: 'catalogo',
    fonteGeradora: 'Arranjo físico inadequado\nAto inseguro',
    possiveisLesoes: 'Torções, contusões, luxações e fraturas',
    embasamentoTecnico: '1.5.4.3.2 da NR 1',
    medidasControle: 'ISSO (Instrução de Segurança e Saúde Ocupacional)\nSinalizações de segurança sempre que necessário.'
  },
  {
    possivelRisco: 'Queda de diferença de nível (< 2m)',
    tipoExposicao: 'Acidente',
    tipo: 'catalogo',
    fonteGeradora: 'Desnível, degraus, rampa ou possíveis depressões no piso\nAto inseguro',
    possiveisLesoes: 'Torções, contusões, luxações e fraturas',
    embasamentoTecnico: '1.5.4.3.2 da NR 1',
    medidasControle: 'ISSO (Instrução de Segurança e Saúde Ocupacional)\nSinalizações de segurança sempre que necessário.'
  }
]
